// Fahrzeuge eines Mitglieds - bis zu drei, jedes mit eigenem Foto, eigenem
// Text und eigenem MINI Bingo.
//
// Reine Logik, ohne Firebase-Import und damit direkt testbar.
// Backend-Spiegel: functions/shared/vehicles.js — **synchron halten**. Der
// Sync-Test src/__tests__/vehiclesSync.test.js ruft beide Fassungen auf und
// vergleicht die Ergebnisse.
//
// Warum die Fahrzeuge als Map am Nutzerdokument liegen und nicht als Liste
// oder Subcollection: siehe den Kopf des Backend-Spiegels. Kurz: Nur eine Map
// erlaubt `vehicles.{id}.bingo.{categoryId}` in Punktschreibweise, und damit
// bleiben gleichzeitige Uploads unabhängig voneinander.

import { MINI_BINGO_CATEGORIES } from './miniBingo';

/** Obergrenze — die Crew-Kachel zeigt genau so viele Bingo-Muster. */
export const MAX_VEHICLES = 3;

/** Höchstens so viele Bingo-Muster stehen auf einer Crew-Kachel. */
export const MAX_CARD_BINGOS = MAX_VEHICLES;

/** ID des Fahrzeugs, das aus den alten flachen Feldern entsteht. */
export const LEGACY_VEHICLE_ID = 'v1';

/** Storage-Ordner der Fahrzeugbilder — siehe storage.rules. */
export const VEHICLE_STORAGE_PREFIX = 'vehicles';

/** Zeitstempel in Millisekunden — Firestore-Timestamp, Date oder Zahl. */
export function millisOf(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  return 0;
}

/** Liegt im alten Bingo-Feld mindestens ein Foto? */
export function hasBingoEntries(bingo) {
  if (!bingo || typeof bingo !== 'object') return false;
  return MINI_BINGO_CATEGORIES.some((c) => Boolean(bingo[c.id]?.imageUrl));
}

const LEGACY_VEHICLE_FIELDS = [
  'carModel',
  'carYear',
  'carPower',
  'carStory',
  'bannerUrl',
  'bannerPath',
];

/** Ist an den alten flachen Feldern überhaupt etwas hinterlegt? */
export function hasLegacyVehicle(userData) {
  return LEGACY_VEHICLE_FIELDS.some((feld) => {
    const wert = userData?.[feld];
    return typeof wert === 'string' && wert.trim() !== '';
  });
}

/**
 * Das Fahrzeug aus den alten flachen Feldern — beim Lesen erzeugt, nicht
 * gespeichert. Damit braucht die Umstellung keinen Migrationslauf: Wer sein
 * Profil nie anfasst, sieht sein Auto trotzdem.
 */
export function legacyVehicle(userData) {
  if (!hasLegacyVehicle(userData) && !hasBingoEntries(userData?.miniBingo)) return null;
  return {
    model: userData?.carModel ?? '',
    // Einen Spitznamen gab es vor der Umstellung nicht.
    nickname: '',
    year: userData?.carYear ?? '',
    power: userData?.carPower ?? '',
    story: userData?.carStory ?? '',
    imageUrl: userData?.bannerUrl ?? '',
    imagePath: userData?.bannerPath ?? '',
    primary: true,
    createdAt: userData?.createdAt ?? null,
    bingo: userData?.miniBingo ?? {},
  };
}

/**
 * Die Fahrzeuge eines Mitglieds als Liste, in stabiler Reihenfolge.
 *
 * **Die einzige Leseschnittstelle.** Profilkopf, Crew-Kachel, Crew-Profil und
 * das eigene Profil gehen ausschließlich hierüber — wer stattdessen direkt
 * `profile.vehicles` liest, sieht bei jedem unveränderten Bestandsprofil
 * nichts.
 */
export function vehiclesOf(userData) {
  const map = userData?.vehicles;
  const vorhanden = map && typeof map === 'object' ? Object.keys(map) : [];

  if (vorhanden.length === 0) {
    const alt = legacyVehicle(userData);
    return alt ? [{ id: LEGACY_VEHICLE_ID, ...alt }] : [];
  }

  return vorhanden
    .map((id) => ({ id, ...map[id] }))
    .sort((a, b) => millisOf(a.createdAt) - millisOf(b.createdAt) || a.id.localeCompare(b.id));
}

/** Das Fahrzeug im Profilkopf und auf der Crew-Kachel. `primary` gewinnt. */
export function primaryVehicleOf(userData) {
  const liste = vehiclesOf(userData);
  return liste.find((v) => v.primary === true) ?? liste[0] ?? null;
}

/** Darf noch eines dazu? */
export function canAddVehicle(userData) {
  return vehiclesOf(userData).length < MAX_VEHICLES;
}

/** Die Bingo-Einträge eines Fahrzeugs — nie undefined. */
export function bingoOf(vehicle) {
  return vehicle?.bingo ?? {};
}

/**
 * Beschriftung eines Fahrzeugs — für den Umschalter und als Überschrift.
 *
 * **Der Spitzname gewinnt gegen das Modell.** Wer seinem Auto einen Namen
 * gibt, nennt es auch so; „Der Rote" unterscheidet zwei MINI Cooper S
 * zuverlässiger als das Modell, das bei beiden gleich lautet. Das Modell
 * verschwindet dabei nicht — es steht als Sachangabe in `vehicleLines`.
 *
 * Fällt zuletzt auf „Mein MINI" zurück: Ein Bestandsprofil kann Bingo-Fotos
 * haben, ohne dass je ein Modell eingetragen wurde. Ein leerer Chip wäre
 * nicht als anklickbar erkennbar.
 */
export function vehicleLabel(vehicle) {
  const nickname = typeof vehicle?.nickname === 'string' ? vehicle.nickname.trim() : '';
  const model = typeof vehicle?.model === 'string' ? vehicle.model.trim() : '';
  return nickname || model || 'Mein MINI';
}

/** Der Spitzname, wenn einer hinterlegt ist — sonst leer. */
export function vehicleNickname(vehicle) {
  const nickname = typeof vehicle?.nickname === 'string' ? vehicle.nickname.trim() : '';
  return nickname;
}

/**
 * Die Eckdaten als eigene Zeilen — Modell, Baujahr, Leistung.
 *
 * Feste Schlüssel statt des Index, damit React sie auch dann auseinanderhält,
 * wenn zwei Angaben gleich lauten (Modell „2016" und Baujahr 2016 sind selten,
 * aber möglich). Gleiche Darstellung wie bisher im Profilkopf.
 */
export function vehicleLines(vehicle) {
  return [
    vehicle?.model && { key: 'model', text: vehicle.model },
    vehicle?.year && { key: 'year', text: String(vehicle.year) },
    vehicle?.power && { key: 'power', text: `${vehicle.power} PS` },
  ].filter(Boolean);
}

/**
 * Storage-Pfad eines Fahrzeugbildes.
 *
 * Mit Zeitstempel statt festem Namen — sonst zeigt der Browser-Cache unter
 * derselben Adresse noch tagelang das alte Bild. Dieselbe Überlegung wie bei
 * `bingoStoragePath`.
 */
export function vehicleImageFolder(uid) {
  return `${VEHICLE_STORAGE_PREFIX}/${uid}`;
}
